import { useTheme } from "styled-components/native";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { Button, CheckBox, Input, Spacer, VDBText } from "@components/atoms";
import { ButtonType } from "@components/atoms/Button/types";
import { schema } from "./schema";
import { useIssueTypes, useReportAnIssue } from "@apis/reportAnIssue";
import { ToastTypes, showToast } from "../VDBToast/util";
import { getButtonCategory } from "../Share/utils/utils";
import Loader from "@components/atoms/Loader";
import { Keyboard, Platform } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import styled from "styled-components/native";
import { ReportAnIssueResponse, ReportAnIssueScreenProps } from "./types";
import { useTranslation } from "react-i18next";
import React from "react";
import { css } from "styled-components/native";

const ReportAnIssueScreen = ({
  onClose,
  itemDetail,
}: ReportAnIssueScreenProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const reportAnIssue = useReportAnIssue();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    resolver: yupResolver(schema),
  });
  const getIssueTypes = useIssueTypes();
  const [selectedIssue, setSelectedIssue] = useState<ReportAnIssueResponse[]>(
    []
  );

  const handlePress = (issue: any) => {
    if (selectedIssue.includes(issue.id)) {
      setSelectedIssue(selectedIssue.filter((id) => id !== issue.id));
    } else {
      setSelectedIssue([...selectedIssue, issue.id]);
    }
  };

  const handleEvent = (data: any) => {
    reportAnIssue.mutate(
      {
        issue_description: data.comments,
        issue_type_ids: selectedIssue,
        item_detail: itemDetail,
        stock_item_id: itemDetail?.id,
      },
      {
        onSuccess(onSuccessData: any) {
          onClose();
          showToast(
            undefined,
            onSuccessData.data.message,
            ToastTypes.SUCCESS,
            true,
            true
          );
        },
      }
    );
  };

  if (getIssueTypes.isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <Overlay>
        <Container>
          <Spacer size={SpaceType.large} />
          <PaddingContanier>
            <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
              {t("reportAnIssue.screenText.text")}
            </VDBText>
            <Spacer size={SpaceType.Double} />
            <RadioButtonContainer>
              {getIssueTypes.data?.data.map(
                (issue: ReportAnIssueResponse, index) => (
                  <React.Fragment key={index}>
                    <RadioContainer>
                      <CheckBox
                        labelColor={theme["core-06"]}
                        style={typography["Body-R"]}
                        text={issue.name}
                        isChecked={selectedIssue.includes(issue.id)}
                        onPress={() => handlePress(issue)}
                      />
                    </RadioContainer>
                  </React.Fragment>
                )
              )}
            </RadioButtonContainer>
            <Spacer size={SpaceType.Basic} />
            <Controller
              control={control}
              render={({
                field: { onChange, onBlur, value },
                formState: { errors },
              }) => (
                <Input
                  inputLabel={t("reportAnIssue.input.placeholder.text")}
                  placeholder={t("reportAnIssue.input.placeholder.text")}
                  leftIcon={undefined}
                  hideIconContainer={true}
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  error={errors?.comments?.message}
                  multiline={true}
                  disabled={reportAnIssue.isLoading}
                  blurOnSubmit={true}
                  onSubmitEditing={() => {
                    Keyboard.dismiss();
                  }}
                  keyboardType="default"
                  returnKeyType="done"
                />
              )}
              name="comments"
            />
          </PaddingContanier>
          <Spacer size={SpaceType.Big} />
        </Container>
      </Overlay>
      {Platform.OS !== "web" ? <BorderLine /> : null}
      <ButtonContainer>
        <ContainerOverlay>
          <Button
            type={ButtonType.primary}
            category={getButtonCategory(itemDetail?.type)}
            title={t("reportAnIssue.button.text")}
            onPress={handleSubmit(handleEvent)}
            disabled={
              !isValid || reportAnIssue.isLoading || selectedIssue.length === 0
            }
            loading={reportAnIssue.isLoading}
          />
        </ContainerOverlay>
      </ButtonContainer>
    </>
  );
};

const Overlay = styled.View`
  min-height: 440px;
  align-items: center;
`;

const Container = styled.View`
  width: 100%;

  ${({ theme }) =>
    !theme.isMobile &&
    css`
      max-width: 608px;
    `}
`;

const RadioButtonContainer = styled.View``;

const BorderLine = styled.View`
  height: 1px;
  background-color: ${({ theme }) => theme["core-03"]};
`;

const LoaderContainer = styled.View`
  min-height: 550px;
`;

const PaddingContanier = styled.View`
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;
const RadioContainer = styled.View`
  padding-top: ${SpaceType.Extra}px;
  padding-bottom: ${SpaceType.Extra}px;
`;

const ButtonContainer = styled.View<{
  itemType?: any;
}>`
  padding-top: ${Platform.OS === "web" ? SpaceType.Double : SpaceType.Big}px;
  padding-bottom: ${Platform.OS === "web"
    ? SpaceType.Double
    : SpaceType.ExtraLarge}px;
  background-color: ${({ theme }) => theme["core-01"]};
  padding-horizontal: ${SpaceType.Double}px;
  align-items: center;
  ${({ theme }) =>
    Platform.OS !== "web" &&
    !theme.isMobile &&
    css`
      padding-horizontal: 0px;
    `}
`;

const ContainerOverlay = styled.View`
  width: 100%;
  ${({ theme }) =>
    Platform.OS !== "web" &&
    !theme.isMobile &&
    css`
      max-width: 343px;
    `}
`;

export default ReportAnIssueScreen;
